// changeTokens — read the `Change-Tokens` header of a live read's answer
// (ADR-074 §3): for each change counter the answer depends on, its name AND the
// change token the answer reflects — an RFC 8941 dictionary of strings,
// `tickets="…", visits="…"`.
//
// TOLERANT, because a tab stays open for weeks and the server moves under it:
//   • a header that does not parse is the RFC's rule — the whole field is
//     ignored: no tokens, so the question is simply not live (the periodic
//     resync still re-reads it);
//   • a member that parses but is not a STRING (a bare key, a number, an inner
//     list) is skipped, parameters are skipped — a newer server may say more
//     than this client understands, and the names it knows still count.
// A token is opaque: kept as given, compared for equality only, never read.

import { CHANGE_TOKENS_HEADER } from '@ems-app/schemas/live/vocabulary';
import type { ChangeTokens } from '@ems-app/schemas/live/responses';

const KEY = /^[a-z*][a-z0-9_.*-]*/;
// Any bare item that is not a string, up to the next `,` or `;` — read only to
// be stepped over (a quote inside it is not a shape this header can take).
const BARE = /^[^,;"]+/;
const PARAM = /^;[ ]*[a-z*][a-z0-9_.*-]*(?:=(?:"(?:[^"\\]|\\["\\])*"|[^,;"]+))?/;

/** Parse the header's value. `null` = absent or malformed (the field is ignored). */
export function parseChangeTokens(value: unknown): ChangeTokens | null {
  if (typeof value !== 'string') return null;
  let rest = value.trim();
  if (rest === '') return null;
  const out: ChangeTokens = {};
  for (;;) {
    const key = KEY.exec(rest)?.[0];
    if (!key) return null;
    rest = rest.slice(key.length);
    if (rest.startsWith('=')) {
      rest = rest.slice(1);
      if (rest.startsWith('"')) {
        const end = closingQuote(rest);
        if (end < 0) return null;
        const token = rest.slice(1, end).replace(/\\(["\\])/g, '$1');
        if (token !== '') out[key] = token;
        rest = rest.slice(end + 1);
      } else if (rest.startsWith('%"')) {
        // A display string (RFC 9651) — not a token: stepped over like any value this client does not read.
        const end = closingQuote(rest.slice(1));
        if (end < 0) return null;
        rest = rest.slice(end + 2);
      } else if (rest.startsWith('(')) {
        const end = closingParen(rest);
        if (end < 0) return null;
        rest = rest.slice(end + 1);
      } else {
        const bare = BARE.exec(rest)?.[0];
        if (!bare) return null;
        rest = rest.slice(bare.length);
      }
    }
    for (let param = PARAM.exec(rest); param; param = PARAM.exec(rest)) rest = rest.slice(param[0].length);
    rest = rest.replace(/^[ \t]+/, '');
    if (rest === '') return out;
    if (!rest.startsWith(',')) return null;
    rest = rest.slice(1).replace(/^[ \t]+/, '');
    // A trailing comma is a parse failure in the RFC.
    if (rest === '') return null;
  }
}

/** The index of the quote closing the string that opens at index 0 (`\"` and `\\` are its only escapes). */
function closingQuote(s: string): number {
  for (let i = 1; i < s.length; i++) {
    if (s[i] === '\\') {
      const next = s[i + 1];
      if (next !== '"' && next !== '\\') return -1;
      i++;
    } else if (s[i] === '"') return i;
  }
  return -1;
}

/** The index of the `)` closing the inner list that opens at index 0 — a `)` inside one of its strings is not it. */
function closingParen(s: string): number {
  for (let i = 1; i < s.length; i++) {
    if (s[i] === '"') {
      const end = closingQuote(s.slice(i));
      if (end < 0) return -1;
      i += end;
    } else if (s[i] === ')') return i;
  }
  return -1;
}

/** An answer's headers, as axios hands them over (an `AxiosHeaders`, whose `get`
 *  ignores case) or as a plain record (a test, a shim). */
type HeadersLike = { get?: unknown } | Record<string, unknown> | null | undefined;

/** The tokens an answer reflects — `null` when it carries none (a read that is not live, a refusal). */
export function changeTokensOf(headers: HeadersLike): ChangeTokens | null {
  if (!headers) return null;
  const get = (headers as { get?: unknown }).get;
  if (typeof get === 'function') return parseChangeTokens((get as (name: string) => unknown).call(headers, CHANGE_TOKENS_HEADER));
  const wanted = CHANGE_TOKENS_HEADER.toLowerCase();
  for (const [name, v] of Object.entries(headers)) if (name.toLowerCase() === wanted) return parseChangeTokens(v);
  return null;
}
